import { BusPrice } from "@/components/site/BusPrice";
import { cityPageForGemeente, isGemeenteIndexable } from "@/lib/gemeente-canonical";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CityHubLinks } from "@/components/site/CityHubLinks";
import { seoTitle } from "@/lib/seo-title";
import { Plane, ShieldCheck } from "lucide-react";
import { shortName } from "@/lib/place-aliases";

import { SearchForm } from "@/components/booking/SearchForm";
import { EnBreadcrumb } from "@/components/en/EnBreadcrumb";
import { EnFixedRoutePrices } from "@/components/en/EnFixedRoutePrices";
import { EnLayout } from "@/components/en/EnLayout";
import { FaqList, faqJsonLd } from "@/components/site/FaqList";
import { LocationImage } from "@/components/site/LocationImage";
import { CityServices } from "@/components/site/CityServices";
import { Button } from "@/components/ui/button";
import { eurEn } from "@/lib/en/local-seo-en";
import {
  enSchipholAnswers,
  enSchipholCity,
  enSchipholFaqs,
  enSchipholIntro,
  enSchipholMeta,
  enSchipholSections,
} from "@/lib/en/schiphol-city-en";
import { hreflangLinks, SITE } from "@/lib/i18n-urls";
import { PLACES_BY_SLUG } from "@/lib/local-seo";
import { absoluteImageUrl, cityImage } from "@/lib/location-images";
import { withMetaCta } from "@/lib/meta-cta";
import { PRICE_CTA_EN } from "@/lib/price-display";
import { hasSchipholPage } from "@/lib/schiphol-city";
import { hasCityServices } from "@/lib/city-services-slugs";

export const Route = createFileRoute("/en/taxi/$place_/schiphol")({
  loader: ({ params }) => {
    const place = PLACES_BY_SLUG.get(params.place);
    if (!place || !hasSchipholPage(place.slug)) throw notFound();
    return { place };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Page not found | Taxiritje.nl" }, { name: "robots", content: "noindex" }],
      };
    }
    const c = enSchipholCity(loaderData.place);
    const m = enSchipholMeta(c);
    const img = cityImage(c.slug);
    const image = img ? absoluteImageUrl(img) : `${SITE}/og/taxiritje-brand.jpg`;
    const description = withMetaCta(m.description, "en");
    return {
      meta: [
        { title: seoTitle(m.title) },
        { name: "description", content: description },
        { name: "robots", content: "index, follow" },
        { property: "og:title", content: m.ogTitle },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: m.url },
        { property: "og:site_name", content: "Taxiritje.nl" },
        { property: "og:locale", content: "en_GB" },
        { property: "og:image", content: image },
        {
          property: "og:image:alt",
          content: `Taxi from ${c.displayName} to Schiphol Airport`,
        },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: m.ogTitle },
        { name: "twitter:description", content: m.description },
        { name: "twitter:image", content: image },
      ],
      links: [{ rel: "canonical", href: m.url }, ...hreflangLinks(m.path)],
    };
  },
  component: EnSchipholCityPage,
  notFoundComponent: EnSchipholNotFound,
  errorComponent: EnSchipholNotFound,
});

function EnSchipholNotFound() {
  return (
    <EnLayout>
      <div className="mx-auto max-w-lg px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold">This Schiphol page does not exist</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Calculate your fixed price online — we drive throughout the Netherlands.
        </p>
        <div className="mt-5 flex justify-center gap-3">
          <Button asChild>
            <Link to="/en/book">Book a taxi</Link>
          </Button>
          <Button asChild variant="secondary">
            <Link to="/en/taxi-schiphol">Schiphol taxi</Link>
          </Button>
        </div>
      </div>
    </EnLayout>
  );
}

function Jsonld({ data }: { data: unknown }) {
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />
  );
}

function EnSchipholCityPage() {
  const { place } = Route.useLoaderData();
  const c = enSchipholCity(place);
  const m = enSchipholMeta(c);
  const intro = enSchipholIntro(c);
  const sections = enSchipholSections(c);
  const answers = enSchipholAnswers(c);
  const faqs = enSchipholFaqs(c);
  const image = cityImage(c.slug);

  return (
    <EnLayout>
      <Jsonld
        data={{
          "@context": "https://schema.org",
          "@type": "TaxiService",
          name: `Taxi ${c.displayName} to Schiphol`,
          description: `Private fixed-price taxi from ${c.displayName} to Schiphol Airport, door to door and available 24/7.`,
          provider: { "@id": `${SITE}/#organization` },
          areaServed: {
            "@type": "City",
            name: c.displayName,
            containedInPlace: { "@type": "AdministrativeArea", name: c.provinceEn },
          },
          serviceType: ["Airport transfer", "Private taxi", "Minibus taxi"],
          ...(c.price != null
            ? {
                offers: {
                  "@type": "Offer",
                  priceCurrency: "EUR",
                  price: c.price,
                  availability: "https://schema.org/InStock",
                  description: `Fixed price per private taxi from ${c.displayName} to Schiphol, up to 4 passengers`,
                  url: `${SITE}/en/taxi/${c.slug}/schiphol`,
                  priceSpecification: {
                    "@type": "PriceSpecification",
                    priceCurrency: "EUR",
                    price: c.price,
                    valueAddedTaxIncluded: true,
                    description: `Fixed total price per taxi from ${c.displayName} to Schiphol, including luggage, tolls and parking`,
                  },
                },
              }
            : {}),
        }}
      />
      <Jsonld data={faqJsonLd(faqs)} />

      <div className="mx-auto max-w-5xl px-4 py-8">
        <EnBreadcrumb
          items={[
            { label: "Service area", to: "/en/taxi" },
            { label: `Taxi ${c.displayName}`, to: `/en/taxi/${c.slug}` },
          ]}
          current="Schiphol"
        />

        <h1 className="mt-4 font-display text-2xl font-bold sm:text-3xl">{m.h1}</h1>

        <div className="card-elevated mt-5 flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {c.displayName} → Schiphol
            </p>
            {c.price != null ? (
              <>
                <p className="mt-1 font-display text-3xl font-bold sm:text-4xl">{eurEn(c.price)}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Fixed price per private taxi, up to 4 passengers — the same amount you pay at
                  checkout.
                </p>
                <BusPrice slug={c.slug} anchor="schiphol" locale="en" />
              </>
            ) : (
              <>
                <p className="mt-1 font-display text-2xl font-bold sm:text-3xl">{PRICE_CTA_EN}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  For this route we calculate your fixed price from your own address.
                </p>
              </>
            )}
          </div>
          <div className="flex flex-col gap-2 sm:w-56">
            <Button asChild size="lg">
              <Link to="/en/book">Book your Schiphol taxi</Link>
            </Button>
            <Button asChild variant="secondary">
              <Link to="/en/taxi/$place" params={{ place: c.slug }}>
                All rides from {c.displayName}
              </Link>
            </Button>
          </div>
        </div>

        <div className="float-panel mt-6 p-4 sm:p-6">
          <SearchForm
            locale="en"
            submitLabel={`Price ${c.displayName} – Schiphol`}
            prefill={{ van: c.displayName, naar: "Schiphol" }}
          />
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-[1.55fr_1fr]">
          <div className="space-y-8">
            <section className="space-y-3">
              {intro.map((paragraph) => (
                <p
                  key={paragraph.slice(0, 28)}
                  className="text-sm leading-relaxed text-muted-foreground"
                >
                  {paragraph}
                </p>
              ))}
              {shortName(c.displayName) && (
                <p className="text-sm leading-relaxed text-muted-foreground">
                  The town name is often written in its short form: a taxi from{" "}
                  {shortName(c.displayName)} to Schiphol is the same ride, at the same fixed price.
                </p>
              )}
            </section>

            <LocationImage image={image} priority />

            {hasCityServices(place.slug) && <CityServices place={place} variant="schiphol" locale={place.slug === "rotterdam" ? "en" : "nl"} />}

            <section className="space-y-3">
              <h2 className="font-display text-xl font-bold">Short answers before you book</h2>
              <dl className="space-y-3">
                {answers.map((a) => (
                  <div key={a.q}>
                    <dt className="text-sm font-semibold text-foreground">{a.q}</dt>
                    <dd className="mt-1 text-sm leading-relaxed text-muted-foreground">{a.a}</dd>
                  </div>
                ))}
              </dl>
            </section>

            {sections.map((s) => (
              <section key={s.heading} className="space-y-3">
                <h2 className="font-display text-xl font-bold">{s.heading}</h2>
                {s.paragraphs.map((p) => (
                  <p key={p.slice(0, 28)} className="text-sm leading-relaxed text-muted-foreground">
                    {p}
                  </p>
                ))}
                {s.list && (
                  <ul className="space-y-1.5 text-sm text-muted-foreground">
                    {s.list.map((item) => (
                      <li key={item}>• {item}</li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            <EnFixedRoutePrices place={place} />

            <section className="space-y-3">
              <h2 className="font-display text-xl font-bold">
                Frequently asked questions about the Schiphol taxi from {c.displayName}
              </h2>
              <FaqList items={faqs} />
            </section>
          </div>

          <aside className="space-y-4">
            <div className="card-elevated p-5">
              <h2 className="font-display text-lg font-bold">Book now</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {c.price != null
                  ? `${eurEn(c.price)} per private taxi, paid online and confirmed straight away.`
                  : "You see your fixed price before you pay."}
              </p>
              <Button asChild className="mt-4 w-full">
                <Link to="/en/book">Reserve your taxi to Schiphol</Link>
              </Button>
              <p className="mt-3 inline-flex items-start gap-1.5 text-xs text-muted-foreground">
                <ShieldCheck className="mt-0.5 size-3.5 text-primary" /> Licensed drivers, luggage
                and tolls included.
              </p>
            </div>

            <div className="card-elevated p-5">
              <h2 className="font-display text-lg font-bold">Useful next reads</h2>
              <ul className="mt-3 space-y-2 text-sm">
                <li>
                  <Link to="/en/taxi/$place" params={{ place: c.slug }} className="hover:underline">
                    Taxi {c.displayName}: all destinations and prices
                  </Link>
                </li>
                <li>
                  <Link to="/en/taxi-schiphol" className="hover:underline">
                    Our Schiphol taxi service explained
                  </Link>
                </li>
                <li>
                  <Link to="/en/airport-taxi" className="hover:underline">
                    Airport transfers to other airports
                  </Link>
                </li>
                <li>
                  <Link to="/en/minibus-taxi-8-passengers" className="hover:underline">
                    Minibus taxi for 5 to 8 passengers
                  </Link>
                </li>
                <li>
                  <Link to="/en/prices" className="hover:underline">
                    Fixed prices per route
                  </Link>
                </li>
                {c.place.gemeenteSlug && isGemeenteIndexable(c.place.gemeenteSlug) ? (
                  <li>
                    <Link to="/en/taxi/gemeente/$municipality" params={{ municipality: c.place.gemeenteSlug }} className="hover:underline">
                      Taxi in the municipality of {c.gemeenteShort}
                    </Link>
                  </li>
                ) : null}
              </ul>
            </div>

            {c.nearby.length > 0 && (
              <div className="card-elevated p-5">
                <h2 className="font-display text-lg font-bold">
                  Schiphol taxi from towns near {c.displayName}
                </h2>
                <ul className="mt-3 space-y-1.5 text-sm">
                  {c.nearby.map((n) => (
                    <li key={n.slug} className="flex items-center gap-1.5">
                      <Plane className="size-3.5 shrink-0 text-primary" />
                      <Link
                        to="/en/taxi/$place/schiphol"
                        params={{ place: n.slug }}
                        className="font-medium hover:underline"
                      >
                        {n.name} to Schiphol
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </div>
      <CityHubLinks locale="en" />
    </EnLayout>
  );
}
